import type { CSSProperties, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { apiGet, TENANT_TOKEN_KEY } from '../../api/client';
import { EmptyState } from '../../design-system/components/EmptyState';
import { tokens } from '../../design-system/tokens';
import { channelLabel, channelSortKey } from './conversationChannelMeta';
import { buildInAppDocsArticleHref } from './help/docsBaseUrl';
import { WORKFLOWS_UI_ENABLED } from '../../config/productFeatures';
import { useAgentSelector } from '../../auth/AgentSelectorContext';

/** "Buenos días/Buenas tardes/Buenas noches" en base a la hora local. */
function timeBasedGreeting(date = new Date()): string {
  const h = date.getHours();
  if (h < 12) return 'Buenos días';
  if (h < 19) return 'Buenas tardes';
  return 'Buenas noches';
}

type DashboardStats = {
  integrations: {
    total: number;
    connected: number;
    disconnected: number;
    byProvider: Record<string, number>;
  };
  conversations: {
    total: number;
    open: number;
    closed: number;
    byChannel: Record<string, number>;
    controlAuto: number;
    controlHuman: number;
    updatedLast7Days: number;
    updatedLast30Days: number;
  };
  messages: {
    total: number;
    visitor: number;
    agent: number;
    last7Days: number;
    /** Respuestas de agente por atajo semántico (= llamadas al modelo evitadas en ese turno). */
    agentRepliesViaShortcut: number;
    agentRepliesViaShortcutLast7Days: number;
  };
  recent: Array<{
    id: string;
    channel: string;
    status: string;
    visitorName: string | null;
    updatedAt: string;
    messageCount: number;
  }>;
  catalog: { agents: number; workflows: number };
};

const providerLabel: Record<string, string> = {
  facebook: 'Messenger',
  instagram: 'Instagram',
  whatsapp_js: 'WhatsApp (QR)',
  whatsapp_meta: 'WhatsApp (Meta)',
  telegram: 'Telegram',
  email: 'Email',
  sms_twilio: 'SMS (Twilio)',
  slack: 'Slack',
  teams: 'Teams',
  web_widget: 'Chat web',
};

export function DashboardPage() {
  const nav = useNavigate();
  const token = localStorage.getItem(TENANT_TOKEN_KEY);
  const { selectedAgentId, selectedAgent } = useAgentSelector();
  const [me, setMe] = useState<{
    user: { name: string | null; email: string } | null;
    organization: { name: string; slug: string };
  } | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsErr, setStatsErr] = useState('');
  const [health, setHealth] = useState<{
    openAiConfigured: boolean;
    anthropicConfigured?: boolean;
    model: string;
    saasDbConfigured?: boolean;
  } | null>(null);

  const loadStats = useCallback(async () => {
    if (!token) return;
    setStatsErr('');
    try {
      const data = await apiGet<DashboardStats>(`/api/console/dashboard-stats${selectedAgentId ? `?agentId=${selectedAgentId}` : ''}`, token);
      setStats(data);
    } catch (e) {
      setStatsErr((e as Error).message);
      setStats(null);
    }
  }, [token, selectedAgentId]);

  useEffect(() => {
    if (!token) return;
    void apiGet<{
      user: { name: string | null; email: string } | null;
      organization: { name: string; slug: string };
    }>('/api/console/me', token).then((d) => {
      setMe({ user: d.user, organization: d.organization });
    });
    void loadStats();
    void fetch('/health')
      .then((r) => r.json())
      .then(setHealth)
      .catch(() => setHealth(null));
  }, [token, loadStats]);

  const channelBars = useMemo(() => {
    if (!stats) return [];
    const entries = Object.entries(stats.conversations.byChannel).sort((a, b) => {
      const da = channelSortKey(a[0]);
      const db = channelSortKey(b[0]);
      if (da !== db) return da - db;
      return b[1] - a[1];
    });
    const max = Math.max(1, ...entries.map(([, n]) => n));
    return entries.map(([ch, n]) => ({ ch, n, pct: Math.round((n / max) * 100) }));
  }, [stats]);

  const providerBars = useMemo(() => {
    if (!stats) return [];
    const entries = Object.entries(stats.integrations.byProvider).sort((a, b) => b[1] - a[1]);
    const max = Math.max(1, ...entries.map(([, n]) => n));
    return entries.map(([p, n]) => ({ p, n, pct: Math.round((n / max) * 100) }));
  }, [stats]);

  const avgMsgs =
    stats && stats.conversations.total > 0
      ? (stats.messages.total / stats.conversations.total).toFixed(1)
      : '—';

  const replyShare =
    stats && stats.messages.total > 0
      ? Math.round((stats.messages.agent / stats.messages.total) * 100)
      : null;

  const greeting = timeBasedGreeting();
  const firstName = me?.user?.name?.split(' ')[0] ?? null;

  return (
    <div style={{ maxWidth: 1180, margin: '0 auto', padding: '28px 24px 40px' }}>
      {/* Header with actions */}
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1
            style={{
              margin: '0 0 4px',
              fontSize: 24,
              fontWeight: 800,
              color: tokens.semantic.textPrimary,
              letterSpacing: '-0.03em',
            }}
          >
            {greeting}
            {firstName ? `, ${firstName}` : ''}
          </h1>
          {me ? (
            <p style={{ margin: 0, fontSize: 14, color: tokens.semantic.textMuted }}>
              {selectedAgent ? (
                <>Datos de <strong style={{ color: tokens.colors.primary[600] }}>{selectedAgent.name}</strong> · </>
              ) : null}
              <strong style={{ color: tokens.semantic.textSecondary }}>{me.organization.name}</strong>
            </p>
          ) : null}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Link to="/app/test" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 8, border: `1px solid ${tokens.semantic.border}`, background: '#fff', color: tokens.semantic.textSecondary, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
            Test chat
          </Link>
          <Link to="/app/agents" style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '8px 14px', borderRadius: 8, border: 'none', background: tokens.colors.primary[600], color: '#fff', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
            Editar agente
          </Link>
        </div>
      </header>

      {statsErr ? (
        <p style={{ color: tokens.colors.error.dark, marginBottom: 16 }}>
          {statsErr}{' '}
          <button type="button" onClick={() => void loadStats()} style={linkBtn}>
            Reintentar
          </button>
        </p>
      ) : null}

      {!stats && !statsErr ? (
        <p style={{ color: tokens.semantic.textMuted }}>Cargando métricas…</p>
      ) : null}

      {stats && stats.integrations.total === 0 && stats.conversations.total === 0 ? (
        <EmptyState
          title="Empezá conectando un canal"
          description="Sumá WhatsApp, Instagram, Messenger o un chat web para que los visitantes lleguen a tus agentes virtuales."
          primaryAction={{
            label: 'Conectar un canal',
            onClick: () => nav('/app/integrations'),
          }}
          secondaryAction={{
            label: 'Guía de canales',
            href: buildInAppDocsArticleHref('consola', 'canales'),
            target: '_blank',
          }}
          style={{ marginBottom: 24 }}
        />
      ) : null}

      {stats ? (
        <>
          <section
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              marginBottom: 24,
            }}
          >
            <KpiCard
              label="Canales conectados"
              value={`${stats.integrations.connected} / ${stats.integrations.total}`}
              hint={`${stats.integrations.disconnected} sin conectar`}
              accent={tokens.colors.brand[600]}
              to="/app/integrations"
            />
            <KpiCard
              label="Conversaciones"
              value={String(stats.conversations.total)}
              hint={`${stats.conversations.open} abiertas · ${stats.conversations.closed} cerradas u otras`}
              accent={tokens.colors.accent[600]}
              to="/app/conversations"
            />
            <KpiCard
              label="Mensajes (7 días)"
              value={String(stats.messages.last7Days)}
              hint={`${stats.conversations.updatedLast7Days} conversaciones tocadas en la semana`}
              accent={tokens.colors.info.main}
            />
            <KpiCard
              label="Mensajes totales"
              value={String(stats.messages.total)}
              hint={
                replyShare !== null
                  ? `~${replyShare}% respuestas de ${WORKFLOWS_UI_ENABLED ? 'agente/flujo' : 'agente'} · ${avgMsgs} msg / conversación`
                  : `Promedio ${avgMsgs} por conversación`
              }
              accent={tokens.colors.accent[700]}
            />
            <KpiCard
              label="Ahorro por atajos (LLM)"
              value={String(stats.messages.agentRepliesViaShortcut)}
              hint={`${stats.messages.agentRepliesViaShortcutLast7Days} en 7 días · respuestas servidas por similitud, sin llamada al modelo`}
              accent={tokens.colors.brand[700]}
              to="/app/learnings"
            />
          </section>

          <div
            style={{
              display: 'grid',
              gap: 20,
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              marginBottom: 24,
            }}
          >
            <Panel
              title="Interacciones (mensajes)"
              subtitle="Volumen acumulado por rol · atajos = respuestas sin LLM en ese turno"
            >
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
                <div style={miniStat}>
                  <div style={miniStatVal}>{stats.messages.visitor}</div>
                  <div style={miniStatLab}>Usuario / visitante</div>
                </div>
                <div style={miniStat}>
                  <div style={miniStatVal}>{stats.messages.agent}</div>
                  <div style={miniStatLab}>Agente / automático</div>
                </div>
                <div style={miniStat}>
                  <div style={miniStatVal}>{stats.messages.agentRepliesViaShortcut}</div>
                  <div style={miniStatLab}>Por atajo (ahorro LLM)</div>
                </div>
              </div>
              <BarSplit left={stats.messages.visitor} right={stats.messages.agent} leftLabel="Visitante" rightLabel="Agente" />
            </Panel>

            <Panel title="Control de conversaciones" subtitle="Modo en el que está el hilo hoy">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div style={miniStat}>
                  <div style={miniStatVal}>{stats.conversations.controlAuto}</div>
                  <div style={miniStatLab}>Automático</div>
                </div>
                <div style={miniStat}>
                  <div style={miniStatVal}>{stats.conversations.controlHuman}</div>
                  <div style={miniStatLab}>Humano en consola</div>
                </div>
              </div>
              <BarSplit
                left={stats.conversations.controlAuto}
                right={stats.conversations.controlHuman}
                leftLabel="Auto"
                rightLabel="Humano"
                leftColor={tokens.colors.brand[500]}
                rightColor={tokens.colors.warning.main}
              />
            </Panel>

            <Panel title="Actividad reciente" subtitle="Conversaciones con actualización en el período">
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: tokens.semantic.textPrimary }}>
                    {stats.conversations.updatedLast7Days}
                  </div>
                  <div style={{ fontSize: 12, color: tokens.semantic.textMuted, fontWeight: 600 }}>Últimos 7 días</div>
                </div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 800, color: tokens.colors.neutral[600] }}>
                    {stats.conversations.updatedLast30Days}
                  </div>
                  <div style={{ fontSize: 12, color: tokens.semantic.textMuted, fontWeight: 600 }}>Últimos 30 días</div>
                </div>
              </div>
              <p style={{ margin: '14px 0 0', fontSize: 13, color: tokens.semantic.textMuted, lineHeight: 1.5 }}>
                Incluye cualquier cambio en el hilo (mensajes, modo, estado). Compará con mensajes de 7 días para ver
                ritmo de intercambio.
              </p>
            </Panel>
          </div>

          <div
            style={{
              display: 'grid',
              gap: 20,
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              marginBottom: 24,
            }}
          >
            <Panel title="Conversaciones por canal" subtitle="Cantidad de hilos por origen">
              {channelBars.length === 0 ? (
                <p style={{ margin: 0, color: tokens.semantic.textSubtle, fontSize: 14 }}>
                  Todavía no hay conversaciones.
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {channelBars.map(({ ch, n, pct }) => (
                    <li key={ch}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, color: tokens.semantic.textSecondary }}>{channelLabel(ch)}</span>
                        <span style={{ color: tokens.semantic.textMuted }}>{n}</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 4, background: tokens.colors.neutral[200], overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            background: tokens.colors.accent[600],
                            borderRadius: 4,
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>

            <Panel title="Integraciones por tipo" subtitle="Filas configuradas en Canales">
              {providerBars.length === 0 ? (
                <p style={{ margin: 0, color: tokens.semantic.textSubtle, fontSize: 14 }}>
                  Creá integraciones desde <Link to="/app/integrations">Canales</Link>.
                </p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {providerBars.map(({ p, n, pct }) => (
                    <li key={p}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                        <span style={{ fontWeight: 600, color: tokens.semantic.textSecondary }}>
                          {providerLabel[p] ?? p}
                        </span>
                        <span style={{ color: tokens.semantic.textMuted }}>{n}</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 4, background: tokens.colors.neutral[200], overflow: 'hidden' }}>
                        <div
                          style={{
                            width: `${pct}%`,
                            height: '100%',
                            background: tokens.colors.brand[600],
                            borderRadius: 4,
                          }}
                        />
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>

          <Panel title="Recientes" subtitle="Últimas conversaciones actualizadas" style={{ marginBottom: 24 }}>
            {stats.recent.length === 0 ? (
              <p style={{ margin: 0, color: tokens.semantic.textSubtle, fontSize: 14 }}>
                No hay actividad todavía.
              </p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr
                      style={{
                        textAlign: 'left',
                        color: tokens.semantic.textMuted,
                        fontSize: 11,
                        textTransform: 'uppercase',
                      }}
                    >
                      <th style={{ padding: '10px 12px' }}>Canal</th>
                      <th style={{ padding: '10px 12px' }}>Visitante</th>
                      <th style={{ padding: '10px 12px' }}>Msgs</th>
                      <th style={{ padding: '10px 12px' }}>Estado</th>
                      <th style={{ padding: '10px 12px' }}>Actualizado</th>
                      <th style={{ padding: '10px 12px' }} />
                    </tr>
                  </thead>
                  <tbody>
                    {stats.recent.map((r) => (
                      <tr key={r.id} style={{ borderTop: `1px solid ${tokens.colors.neutral[100]}` }}>
                        <td style={{ padding: '10px 12px', fontWeight: 600, color: tokens.semantic.textPrimary }}>
                          {channelLabel(r.channel)}
                        </td>
                        <td style={{ padding: '10px 12px', color: tokens.colors.neutral[600] }}>
                          {r.visitorName ?? '—'}
                        </td>
                        <td style={{ padding: '10px 12px' }}>{r.messageCount}</td>
                        <td style={{ padding: '10px 12px' }}>{r.status}</td>
                        <td style={{ padding: '10px 12px', color: tokens.semantic.textMuted, whiteSpace: 'nowrap' }}>
                          {new Date(r.updatedAt).toLocaleString('es-UY', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <Link to={`/app/conversations/${r.id}`} style={tableLink}>
                            Abrir
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>

          <section
            style={{
              display: 'grid',
              gap: 16,
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              marginBottom: 24,
            }}
          >
            <div style={quickCard}>
              <strong>Catálogo</strong>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: tokens.semantic.textMuted }}>
                {stats.catalog.agents} agente(s)
                {WORKFLOWS_UI_ENABLED ? ` · ${stats.catalog.workflows} flujo(s)` : ''}
              </p>
              <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Link to="/app/agents" style={pillLink}>
                  Agentes
                </Link>
                {WORKFLOWS_UI_ENABLED ? (
                  <Link to="/app/workflows" style={pillLink}>
                    Flujos
                  </Link>
                ) : null}
              </div>
            </div>
            <div style={quickCard}>
              <strong>Operación</strong>
              <p style={{ margin: '8px 0 0', fontSize: 14, color: tokens.semantic.textMuted }}>
                Canales y conversaciones en vivo.
              </p>
              <div style={{ marginTop: 12, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <Link to="/app/integrations" style={pillLink}>
                  Canales
                </Link>
                <Link to="/app/conversations" style={pillLink}>
                  Conversaciones
                </Link>
              </div>
            </div>
          </section>
        </>
      ) : null}

      {health ? (
        <section style={healthBox}>
          <h2 style={{ fontSize: 15, margin: '0 0 10px', fontWeight: 700, color: tokens.semantic.textPrimary }}>
            Motor IA (servicio)
          </h2>
          <p style={{ margin: 0, fontSize: 14, color: tokens.colors.neutral[600] }}>
            OpenAI: {health.openAiConfigured ? 'configurada' : 'no configurada'}
            {health.anthropicConfigured != null
              ? ` · Anthropic (Claude): ${health.anthropicConfigured ? 'configurada' : 'no configurada'}`
              : ''}
            {' '}
            · Modelo por defecto (servicio):{' '}
            <code style={{ background: tokens.colors.neutral[100], padding: '2px 6px', borderRadius: 4 }}>
              {health.model}
            </code>
          </p>
        </section>
      ) : null}
    </div>
  );
}

function KpiCard({
  label,
  value,
  hint,
  accent,
  to,
}: {
  label: string;
  value: string;
  hint: string;
  accent: string;
  to?: string;
}) {
  const inner = (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: tokens.semantic.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {label}
        </div>
        <div style={{ width: 28, height: 28, borderRadius: 6, background: `${accent}14`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: accent }} />
        </div>
      </div>
      <div
        style={{
          fontSize: 26,
          fontWeight: 800,
          color: tokens.semantic.textPrimary,
          letterSpacing: '-0.02em',
        }}
      >
        {value}
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 12, color: tokens.semantic.textMuted, lineHeight: 1.45 }}>{hint}</p>
    </>
  );
  return to ? (
    <Link to={to} className="va-dashboard-kpi" style={kpiCardLink}>
      {inner}
    </Link>
  ) : (
    <div className="va-dashboard-kpi" style={kpiCard}>{inner}</div>
  );
}

function Panel({
  title,
  subtitle,
  children,
  style: extra,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  style?: CSSProperties;
}) {
  return (
    <section style={{ ...panel, ...extra }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: tokens.semantic.textPrimary, margin: '0 0 4px' }}>{title}</h2>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: tokens.semantic.textMuted }}>{subtitle}</p>
      {children}
    </section>
  );
}

function BarSplit({
  left,
  right,
  leftLabel,
  rightLabel,
  leftColor = tokens.colors.accent[600],
  rightColor = tokens.colors.accent[500],
}: {
  left: number;
  right: number;
  leftLabel: string;
  rightLabel: string;
  leftColor?: string;
  rightColor?: string;
}) {
  const t = left + right;
  const lp = t > 0 ? Math.round((left / t) * 100) : 50;
  const rp = 100 - lp;
  return (
    <div style={{ marginTop: 16 }}>
      <div
        style={{
          display: 'flex',
          height: 10,
          borderRadius: 5,
          overflow: 'hidden',
          background: tokens.colors.neutral[200],
        }}
      >
        <div style={{ width: `${lp}%`, background: leftColor }} title={`${leftLabel}: ${left}`} />
        <div style={{ width: `${rp}%`, background: rightColor }} title={`${rightLabel}: ${right}`} />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: 11,
          color: tokens.semantic.textSubtle,
          marginTop: 6,
        }}
      >
        <span>
          {leftLabel} {lp}%
        </span>
        <span>
          {rightLabel} {rp}%
        </span>
      </div>
    </div>
  );
}

const kpiCard: CSSProperties = {
  background: tokens.semantic.surface,
  borderRadius: 12,
  border: `1px solid ${tokens.semantic.border}`,
  padding: 18,
};

const kpiCardLink: CSSProperties = {
  ...kpiCard,
  textDecoration: 'none',
  color: 'inherit',
  display: 'block',
  transition: 'box-shadow 0.15s ease, transform 0.15s ease, border-color 0.15s ease',
};

const panel: CSSProperties = {
  background: tokens.semantic.surface,
  borderRadius: 12,
  border: `1px solid ${tokens.semantic.border}`,
  padding: 22,
};

const miniStat: CSSProperties = {
  padding: 14,
  background: tokens.semantic.surfaceMuted,
  borderRadius: 10,
  border: `1px solid ${tokens.semantic.border}`,
};

const miniStatVal: CSSProperties = { fontSize: 22, fontWeight: 800, color: tokens.semantic.textPrimary };
const miniStatLab: CSSProperties = { fontSize: 12, color: tokens.semantic.textMuted, marginTop: 4, fontWeight: 500 };

const quickCard: CSSProperties = {
  ...kpiCard,
  padding: 18,
};

const pillLink: CSSProperties = {
  display: 'inline-block',
  padding: '8px 14px',
  borderRadius: 8,
  background: tokens.colors.accent[50],
  color: tokens.colors.accent[700],
  fontSize: 13,
  fontWeight: 600,
  textDecoration: 'none',
};

const tableLink: CSSProperties = {
  color: tokens.colors.accent[600],
  fontWeight: 600,
  textDecoration: 'none',
};

const healthBox: CSSProperties = {
  padding: 18,
  background: tokens.semantic.surface,
  borderRadius: 14,
  border: `1px solid ${tokens.semantic.border}`,
};

const linkBtn: CSSProperties = {
  background: 'none',
  border: 'none',
  color: tokens.colors.accent[600],
  cursor: 'pointer',
  textDecoration: 'underline',
  fontSize: 'inherit',
};
